<template>
  <article class="teaser-block" :class="computedPropStyling">
    <SmartLink
      :to="url"
      :tag="tag"
      :target="target"
      :rel="rel"
      :label="`${ariaLabelPrefix} ${teaserTitle}`"
      class="link"
      @click="handleClick"
    >
      <div class="image-wrapper">
        <DynamicImage
          v-if="teaserImage"
          :renditions="computedRenditions"
          :image="teaserImage"
          :loading="loading"
          :priority="priority"
          :variant="props.variant === 'dark' ? props.variant : 'regular'"
          class="block-image"
        >
          <div v-if="icon" class="icon-wrapper">
            <Icon :name="icon" class="icon" role="presentation" />
          </div>

          <div class="badge-wrapper">
            <Badge
              v-if="video"
              :title="computedTitle"
              icon="eenvandaag-general:play-solid"
              size="medium"
            />

            <Badge v-if="external" icon="eenvandaag-general:external-2" size="medium" />
          </div>
        </DynamicImage>
      </div>

      <div class="meta" :class="computedPropStyling">
        <Label
          v-if="computedShowLabel"
          :title="teaserLabel"
          type="text"
          class="label"
          :variant="props.variant"
        />
        <span v-if="!showLabel && subTitle" class="sub-title">{{ subTitle }}</span>
        <Heading
          v-if="!ariaHidden"
          :level="headingLevel"
          :content="teaserTitle as string"
          class="heading"
          :class="headingSize"
          :max-lines="teaserLabel && teaserLabel !== 'artikel' ? 5 : 6"
        />
        <DateTime v-if="showDate && !external" v-bind="props" class="date" />
      </div>
    </SmartLink>
  </article>
</template>

<script setup lang="ts">
import type {
  IAnalytics,
  ICollectionTeaser,
  IImageLoadingSettings,
  TAspectRatio,
} from '~~/types/local_types';
import useArticleData from '~/composables/useArticleData';
import formatUtils from '~/utils/formatUtils';
import DateTime from '~/components/DateTime.vue';

interface IProps extends IImageLoadingSettings, ICollectionTeaser {
  aspectRatio?: '3x2' | '4x3' | '16x9';
  padded?: boolean;
  variant?: 'opaque' | 'dark';
  showDate?: boolean;
  showLabel?: boolean;
  headingLevel?: number;
  icon?: string;
  analytics?: IAnalytics;
  subTitle?: string;
  headingSize?: 'title-lg' | 'title-md';
  ariaLabelPrefix?: string;
  ariaHidden?: boolean;
  tag?: 'NuxtLink' | 'a';
}

const props = withDefaults(defineProps<IProps>(), {
  aspectRatio: '4x3',
  loading: 'lazy',
  priority: 'auto',
  padded: false,
  variant: undefined,
  showDate: false,
  showLabel: true,
  headingLevel: 3,
  icon: undefined,
  analytics: undefined,
  subTitle: '',
  headingSize: 'title-md',
  ariaLabelPrefix: '',
  ariaHidden: false,
  tag: 'NuxtLink',
});

const {
  image: teaserImage,
  label: teaserLabel,
  title: teaserTitle,
  url,
  target,
  rel,
  video,
  external,
} = useArticleData(props);

const { formatIsoDurationNoZeros } = formatUtils();

const computedRenditions = computed(() => {
  return [
    {
      breakpoint: 'sm',
      size: 480,
      aspectRatio: (props.aspectRatio || '3x2') as TAspectRatio,
    },
    {
      breakpoint: 'md',
      size: 280,
      aspectRatio: (props.aspectRatio || '3x2') as TAspectRatio,
    },
  ];
});

const computedPropStyling = computed(() => {
  const classes = [];

  if (props.padded) {
    classes.push('is--padded');
  }

  if (props.variant) {
    classes.push(`is--${props.variant}`);
  }
  return classes;
});

const computedShowLabel = computed(() => {
  return props.showLabel && teaserLabel && teaserLabel !== 'artikel';
});

const handleClick = () => {
  const analyticsDefaults = {
    linkText: teaserTitle,
    linkUrl: url,
    linkAnalyticsTag: 'teaser',
  };
  gtmTrackEvent({ ...analyticsDefaults, ...props?.analytics });
};

const computedTitle = computed(() => {
  if (!props.mainEmbed || !props.mainEmbed[0]) return;
  return formatIsoDurationNoZeros(props.mainEmbed[0]?.contentEmbedsId?.duration);
});
</script>

<style scoped lang="postcss">
.teaser-block {
  &.is--opaque .link {
    background-color: hsl(var(--color-neutral-white));
  }

  &.is--dark .link {
    .meta {
      background-color: hsla(var(--color-neutral-white), 0.15);
      height: 100%;

      &:deep(h3) {
        color: hsl(var(--color-neutral-white));
      }
    }

    &:hover .heading {
      color: hsl(var(--color-primary-200));
    }
  }
}

.link {
  display: block;
  position: relative;
  overflow: hidden;
  height: 100%;

  @add-mixin hover {
    .heading {
      color: hsl(var(--color-primary-750));
    }

    .block-image:deep(.image) {
      scale: 1.05;
    }
  }

  &:active {
    .heading {
      color: var(--color-primary-950);
    }
  }

  @add-mixin focus light {
  }
}

.image-wrapper {
  overflow: hidden;
}

.block-image:deep(.image) {
  transition: scale 250ms ease-in;
  transform-origin: center center;
}

.meta {
  --meta-padding: var(--unit-12) 0;
  --meta-gap: var(--unit-4);

  display: flex;
  flex-direction: column;
  gap: var(--unit-4);
  padding: var(--meta-padding);

  @media (--lg-viewport) {
    --meta-gap: var(--unit-6);
  }

  &.is--padded {
    --meta-padding: var(--unit-12) var(--unit-16) var(--unit-18) var(--unit-16);

    @media (--lg-viewport) {
      --meta-padding: var(--unit-20) var(--unit-24) var(--unit-26) var(--unit-24);
    }
  }

  .heading {
    color: hsl(var(--color-primary-950));
    transition: color 250ms ease-in;

    &.title-lg {
      @mixin title-lg {
      }
    }
    &.title-md {
      @mixin title-md {
      }
    }
  }

  .label {
    display: block;
  }

  .sub-title {
    color: hsl(var(--color-primary-700));
    @mixin heading-xs {
    }
  }

  .date {
    color: hsl(var(--color-primary-600));
    @mixin label-md {
    }
  }
}

.badge-wrapper {
  --badge-offset: var(--unit-4);

  position: absolute;
  bottom: var(--badge-offset);
  left: var(--badge-offset);

  @media (--md-viewport) {
    --badge-offset: var(--unit-8);
  }
}

.icon-wrapper {
  --icon-size: var(--unit-44);
  --offset: calc(var(--base-spacing) * 4);

  position: absolute;
  left: var(--offset);
  top: var(--offset);
  width: var(--icon-size);
  height: var(--icon-size);
  border-radius: 50%;
  background: var(--effect-gradient-brand);
  box-shadow:
    0px 2.526px 5.053px 0px rgba(0, 0, 0, 0.12),
    0px 0px 30.316px 0px rgba(0, 0, 0, 0.25),
    0px -1.263px 0px 0px rgba(0, 0, 0, 0.12) inset;
  align-self: flex-start;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (--lg-viewport) {
    --icon-size: var(--unit-48);
    --offset: calc(var(--base-spacing) * 5);
  }
}

.icon {
  font-size: var(--unit-24);
  color: hsl(var(--color-neutral-white));
}
</style>
